import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useMemo, useCallback, useRef, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import { Background, Controls, MiniMap, Panel } from "@xyflow/react";
import { PROCESS_FLOW_REGISTRY, buildNodeOverlayMap } from "./core";
import { buildSuggestionInfoMap, hasRenderableEventContent } from "./core/builder";
import { FlowCanvas, computePathToNode, useFlow, useIdentityFingerprint } from "../flow-kit";
import { createProcessFlowAdapter, PROCESS_FLOW_LANE_ADAPTER } from "./flow-adapters";
import { FlowViewportHandler } from "./viewportFit";
import { buildNoteNodes, buildNoteConnectorEdges } from "./core/notes";
import { classifyCanvasNode } from "./core/nodeKind";
import ProcessFlowStepDetailSidebar from "../step-detail/ProcessFlowStepDetailSidebar";
// Module-scoped so `useIdentityFingerprint`'s memo stays stable across
// renders (inline closures would re-create every render and defeat the memo).
const stepKey = (s)=>s._id;
const assignmentKey = (a)=>`${a._id}:${a.status}`;
const identityKey = (s)=>s;
// `eventKind` is part of the key: a kind flip (e.g. a second start event
// appearing) changes which nodes the builder emits, so the layout must rerun.
const eventKey = (e)=>`${e._id}:${e.eventKind ?? ""}`;
const laneColorKey = (entry)=>`${entry.laneKey}:${entry.color}`;
const adapter = createProcessFlowAdapter(PROCESS_FLOW_REGISTRY);
export const ProcessFlowVisualization = /*#__PURE__*/ forwardRef(function ProcessFlowVisualization({ process, height = "500px", showMinimap = true, showControls = true, highlights, edgeHighlights, showSensitiveFields, showVariantCheck, overlays, onNodeOverlayClick, controlledSelectedNodeId, onSelectedNodeChange, fitRequest, showStepDetail = true, scrollBehavior = "zoom", renderStepDetail, savedPositions, openAssignments, swimlaneOrder, laneColors, assigneeLookup, entitySuggestions, events, notes, showUnattachedIntermediateEvents, fitViewNodeIds }, ref) {
    const { t } = useTranslation();
    // Controlled/uncontrolled pattern for node selection
    const isControlled = controlledSelectedNodeId !== undefined;
    const [uncontrolledNodeId, setUncontrolledNodeId] = useState(null);
    const selectedNodeId = isControlled ? controlledSelectedNodeId : uncontrolledNodeId;
    const setSelectedNodeId = useCallback((id)=>{
        if (isControlled) {
            onSelectedNodeChange?.(id);
        } else {
            setUncontrolledNodeId(id);
        }
    }, [
        isControlled,
        onSelectedNodeChange
    ]);
    // Step IDs fingerprint detects additions/removals (handles PreviewDialog synthetic process)
    const stepFingerprint = useIdentityFingerprint(process?.processStepRecordings, stepKey, ",");
    const overlayMap = useMemo(()=>buildNodeOverlayMap(overlays), [
        overlays
    ]);
    // Content fingerprint — two assignment sets of the same size but different
    // ids/statuses must produce different cache keys, or a stale layout sticks.
    const openAssignmentsFingerprint = useIdentityFingerprint(openAssignments, assignmentKey);
    const swimlaneOrderFingerprint = useIdentityFingerprint(swimlaneOrder, identityKey);
    // Colors don't affect layout, but the assembly bakes lane tones into node
    // data — without a fingerprint a color change never re-runs assembly.
    const laneColorsFingerprint = useIdentityFingerprint(laneColors, laneColorKey);
    const eventsFingerprint = useIdentityFingerprint(events, eventKey);
    // Swimlane resolution depends on the lookup: `assignedTo` on display steps
    // is always an unpopulated `{ type, id }` ref, so without the lookup every
    // assignee lane degrades to the recorder fallback. Re-layout when the org
    // lists land — sizes are a sufficient fingerprint because the lists only
    // transition empty → loaded within a session.
    const assigneeLookupFingerprint = [
        assigneeLookup?.users?.size ?? 0,
        assigneeLookup?.orgFunctions?.size ?? 0,
        assigneeLookup?.systems?.size ?? 0,
        assigneeLookup?.externalContacts?.size ?? 0
    ].join(":");
    // Derive the canvas's by-id suggestion map (proposed name + status) from the
    // flat array. Memoized so the source/cacheKey only churn when the suggestion
    // set actually changes.
    const suggestionInfoById = useMemo(()=>buildSuggestionInfoMap(entitySuggestions), [
        entitySuggestions
    ]);
    // Re-layout when the suggestion set lands so proposed placeholders resolve
    // their names. Size is a sufficient fingerprint — the map only transitions
    // empty → loaded within a session, like `assigneeLookup`.
    const suggestionsFingerprint = suggestionInfoById.size;
    const source = useMemo(()=>({
            process,
            options: {
                openAssignments,
                swimlaneOrderOverride: swimlaneOrder,
                laneColorsOverride: laneColors,
                events,
                assigneeLookup,
                suggestionsById: suggestionInfoById,
                showUnattachedIntermediateEvents
            }
        }), [
        process,
        openAssignments,
        swimlaneOrder,
        laneColors,
        events,
        assigneeLookup,
        suggestionInfoById,
        showUnattachedIntermediateEvents
    ]);
    const savedPositionsMap = useMemo(()=>{
        if (!savedPositions?.length) return undefined;
        return new Map(savedPositions.map((p)=>[
                p.stepId,
                {
                    x: p.x,
                    y: p.y
                }
            ]));
    }, [
        savedPositions
    ]);
    const flowEnabled = !!process?.processStepRecordings?.length || (openAssignments?.length ?? 0) > 0 || hasRenderableEventContent(events, showUnattachedIntermediateEvents);
    const { nodes: positionedNodes, edges: baseEdges, isLoading: layoutLoading } = useFlow({
        adapter,
        source,
        cacheKey: [
            "process-flow",
            process?._id ?? "",
            process?.updatedAt,
            process?.processStepRecordings?.length ?? 0,
            stepFingerprint,
            openAssignmentsFingerprint,
            swimlaneOrderFingerprint,
            laneColorsFingerprint,
            eventsFingerprint,
            assigneeLookupFingerprint,
            suggestionsFingerprint
        ],
        enabled: flowEnabled,
        savedPositions: savedPositionsMap,
        laneLayout: PROCESS_FLOW_LANE_ADAPTER.laneLayout
    });
    const layoutReady = !layoutLoading && positionedNodes.length > 0;
    // Sticky notes (ADR 0008): appended AFTER layout so ELK never relocates them.
    // Connectors reference the laid-out node ids (orphan targets are skipped).
    //
    // Withhold notes until the process layout has resolved (`useFlow` returns
    // empty positioned nodes while ELK runs async). Notes carry explicit geometry
    // so they measure instantly; if they paint while the process nodes are still
    // empty, they become the only nodes on the canvas and React Flow's one-shot
    // init `fitView` frames the notes instead of the whole process.
    const noteNodes = useMemo(()=>layoutLoading ? [] : buildNoteNodes(notes ?? [], "view"), [
        notes,
        layoutLoading
    ]);
    const noteEdges = useMemo(()=>{
        if (layoutLoading || !notes?.length) return [];
        const presentIds = new Set(positionedNodes.map((n)=>n.id));
        return buildNoteConnectorEdges(notes, presentIds);
    }, [
        notes,
        positionedNodes,
        layoutLoading
    ]);
    const fitViewOptions = useMemo(()=>fitViewNodeIds ? {
            padding: 0.15,
            includeHiddenNodes: false,
            nodes: fitViewNodeIds.map((id)=>({
                    id
                }))
        } : {
            padding: 0.2,
            includeHiddenNodes: false
        }, [
        fitViewNodeIds
    ]);
    // Compute the path of nodes/edges leading to the selected node
    const pathToSelected = useMemo(()=>computePathToNode(selectedNodeId, baseEdges), [
        selectedNodeId,
        baseEdges
    ]);
    const highlightMap = useMemo(()=>{
        const map = new Map();
        if (highlights) {
            for (const h of highlights){
                map.set(h.nodeId, h.style);
            }
        }
        return map;
    }, [
        highlights
    ]);
    // A selection focus exists when the user has pinned a specific node (via
    // click or external `selected`/`on-path` highlight) to trace its path.
    // `current` on its own is a context marker (e.g. the live recording step),
    // not a focus — it highlights the node but should not dim the rest of the
    // canvas, and a `current` node is itself never dimmed by someone else's
    // focus (a finding can cite several steps while only one is selected).
    const hasSelectionFocus = useMemo(()=>{
        if (selectedNodeId) return true;
        for (const style of highlightMap.values()){
            if (style === "selected" || style === "on-path") return true;
        }
        return false;
    }, [
        selectedNodeId,
        highlightMap
    ]);
    const nodes = useMemo(()=>{
        const mapped = positionedNodes.map((node)=>{
            const nodeOverlay = overlayMap.get(node.id);
            const highlightStyle = highlightMap.get(node.id);
            const isOnPath = pathToSelected.nodeIds.has(node.id) || highlightStyle === "on-path";
            const isSelected = node.id === selectedNodeId || highlightStyle === "selected";
            return {
                ...node,
                data: {
                    ...node.data,
                    mode: "view",
                    isSelected,
                    isCurrentStep: highlightStyle === "current",
                    isOnPath,
                    isDimmed: hasSelectionFocus && !isSelected && !isOnPath && highlightStyle !== "current",
                    overlayColor: nodeOverlay?.color,
                    overlayLabel: nodeOverlay?.label,
                    overlayBadges: nodeOverlay?.badges
                }
            };
        });
        return noteNodes.length ? [
            ...mapped,
            ...noteNodes
        ] : mapped;
    }, [
        positionedNodes,
        selectedNodeId,
        highlightMap,
        hasSelectionFocus,
        pathToSelected,
        overlayMap,
        noteNodes
    ]);
    const highlightedEdgePairs = useMemo(()=>{
        if (!edgeHighlights?.length) return null;
        return new Set(edgeHighlights.map((pair)=>`${pair.sourceId}→${pair.targetId}`));
    }, [
        edgeHighlights
    ]);
    const edges = useMemo(()=>{
        const emphasize = (edge)=>({
                ...edge,
                zIndex: 1,
                style: {
                    ...edge.style,
                    stroke: "var(--color-blue-600)",
                    strokeWidth: 4,
                    opacity: 1
                },
                data: {
                    ...edge.data,
                    isOnPath: true
                }
            });
        const styled = !hasSelectionFocus && !highlightedEdgePairs ? baseEdges : baseEdges.map((edge)=>{
            // An explicitly highlighted edge always gets the emphasized look,
            // regardless of the current node selection.
            if (highlightedEdgePairs?.has(`${edge.source}→${edge.target}`)) {
                return emphasize(edge);
            }
            if (!hasSelectionFocus) return edge;
            if (pathToSelected.edgeIds.has(edge.id)) return emphasize(edge);
            return {
                ...edge,
                zIndex: 0,
                style: {
                    ...edge.style,
                    stroke: "var(--color-gray-300)",
                    strokeWidth: 1.5,
                    opacity: 0.3
                }
            };
        });
        return noteEdges.length ? [
            ...styled,
            ...noteEdges
        ] : styled;
    }, [
        baseEdges,
        pathToSelected,
        hasSelectionFocus,
        highlightedEdgePairs,
        noteEdges
    ]);
    const selectedNodeIdRef = useRef(selectedNodeId);
    selectedNodeIdRef.current = selectedNodeId;
    const handleNodeClick = useCallback((_, node)=>{
        if (onNodeOverlayClick && overlayMap.has(node.id)) {
            onNodeOverlayClick(node.id);
        }
        const kind = classifyCanvasNode(node);
        const isSelectable = kind === "step" || kind === "end" || kind === "decision";
        if (isSelectable) {
            setSelectedNodeId(selectedNodeIdRef.current === node.id ? null : node.id);
        }
    }, [
        onNodeOverlayClick,
        overlayMap,
        setSelectedNodeId
    ]);
    const handlePaneClick = useCallback(()=>{
        setSelectedNodeId(null);
    }, [
        setSelectedNodeId
    ]);
    // Read-only selection deliberately differs from the editor's
    // `resolveSelection`: there is no successor slot to resolve here, so an end
    // node opens its PREDECESSOR step's detail instead of an `unclear-end`
    // target.
    const selectedStep = useMemo(()=>{
        if (!selectedNodeId || !process?.processStepRecordings) return null;
        const direct = process.processStepRecordings.find((step)=>step._id === selectedNodeId);
        if (direct) return direct;
        const endNode = positionedNodes.find((n)=>n.id === selectedNodeId && classifyCanvasNode(n) === "end");
        const predId = endNode?.data?.predecessorStepId;
        if (typeof predId === "string") {
            return process.processStepRecordings.find((step)=>step._id === predId) || null;
        }
        return null;
    }, [
        selectedNodeId,
        process,
        positionedNodes
    ]);
    if (!process || !flowEnabled) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex items-center justify-center bg-surface-subtle border border-line rounded-lg",
            style: {
                height
            },
            children: /*#__PURE__*/ _jsxs("div", {
                className: "text-center text-muted-foreground",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm font-medium",
                        children: t("processFlow.noStepsRecorded")
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs mt-1",
                        children: t("processFlow.startRecordingHint")
                    })
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        style: {
            height
        },
        className: "bg-white border border-line rounded-lg overflow-hidden",
        children: /*#__PURE__*/ _jsxs(FlowCanvas, {
            adapter: adapter,
            nodes: nodes,
            edges: edges,
            onNodeClick: handleNodeClick,
            onPaneClick: handlePaneClick,
            proOptions: {
                hideAttribution: true
            },
            fitView: true,
            fitViewOptions: fitViewOptions,
            minZoom: 0.1,
            maxZoom: 2,
            nodesDraggable: false,
            nodesConnectable: false,
            elementsSelectable: false,
            // Both flags together, always: zooming and swallowing the wheel are
            // separate React Flow behaviours, and turning off only the first leaves
            // an embedded canvas eating scroll while doing nothing with it. Passing
            // `true` explicitly restates React Flow's own defaults, so the callers
            // that never set `scrollBehavior` are unaffected.
            zoomOnScroll: scrollBehavior === "zoom",
            preventScrolling: scrollBehavior === "zoom",
            // Read-only viewer: keyboard deletion must stay off even if
            // selectability is ever enabled (React Flow defaults to Backspace).
            deleteKeyCode: null,
            children: [
                /*#__PURE__*/ _jsx(FlowViewportHandler, {
                    flowRef: ref,
                    fitRequest: fitRequest,
                    layoutReady: layoutReady,
                    savedPositions: savedPositionsMap
                }),
                /*#__PURE__*/ _jsx(Background, {}),
                showControls && /*#__PURE__*/ _jsx(Controls, {
                    position: "top-right",
                    orientation: "horizontal",
                    showInteractive: false
                }),
                showMinimap && /*#__PURE__*/ _jsx(MiniMap, {
                    position: "bottom-left"
                }),
                showStepDetail && selectedStep && /*#__PURE__*/ _jsx(Panel, {
                    position: "top-right",
                    style: {
                        maxWidth: "400px",
                        width: "400px",
                        top: "40px",
                        bottom: "60px",
                        right: "10px"
                    },
                    children: renderStepDetail ? renderStepDetail(selectedStep, ()=>setSelectedNodeId(null)) : /*#__PURE__*/ _jsx(ProcessFlowStepDetailSidebar, {
                        step: selectedStep,
                        onClose: ()=>setSelectedNodeId(null),
                        showSensitiveFields: showSensitiveFields,
                        showVariantCheck: showVariantCheck,
                        assigneeLookup: assigneeLookup,
                        entitySuggestions: entitySuggestions
                    })
                })
            ]
        })
    });
});

//# sourceMappingURL=ProcessFlowVisualization.js.map